import { Reference } from '@apollo/client';
import { getStoreKeyName } from '@apollo/client/utilities';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  CommunityFeedPostFragmentDoc,
  MediaInput,
  useCommunitiesV2AddPostMutation,
} from 'generated/graphql';
import { upload } from '@client/components/ajax';
import { pickObject } from '@client/new/helpers/general';
import { CommunityId } from '../model/community.types';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';

const FEED_POSTS_STORE_KEY = getStoreKeyName('posts', {
  direction: undefined,
  lastId: undefined,
});

const MEDIA_INPUT_KEYS = [
  'audio',
  'cdn01',
  'height',
  'image',
  'mimeType',
  'source',
  'video',
  'width',
] as const;

type AddPostInput = { text: string; media: File[] };

export const useAddPost = (
  communityId: CommunityId,
  influencerId?: string | number,
) => {
  const [addPostMutation] = useCommunitiesV2AddPostMutation();
  const creatorId = resolveInfluencerId(influencerId);

  return useCallback(
    async ({ text, media }: AddPostInput): Promise<boolean> => {
      try {
        let mediaInput: MediaInput[] = [];
        if (media.length > 0) {
          if (creatorId === null) throw new Error('Missing creator id');
          const uploaded = await Promise.all(
            media.map((file) =>
              upload(file, creatorId, {
                skipCompression: file.type.startsWith('video'),
              }),
            ),
          );
          mediaInput = uploaded.map((item) =>
            pickObject(item, MEDIA_INPUT_KEYS),
          );
        }

        await addPostMutation({
          variables: { communityId, text, media: mediaInput },
          update: (cache, { data }) => {
            const created = data?.addPost;
            if (!created) return;

            const ref = cache.writeFragment({
              fragment: CommunityFeedPostFragmentDoc,
              fragmentName: 'CommunityFeedPost',
              data: created,
            });
            if (!ref) return;

            cache.modify({
              id: cache.identify({ __typename: 'Community', id: communityId }),
              fields: {
                posts: (
                  existing: readonly Reference[] = [],
                  { storeFieldName, readField },
                ) => {
                  if (storeFieldName !== FEED_POSTS_STORE_KEY) return existing;
                  const present = existing.some(
                    (postRef) => readField('id', postRef) === created.id,
                  );
                  return present ? existing : [ref, ...existing];
                },
              },
            });
          },
        });
        return true;
      } catch {
        toast.error('Something went wrong');
        return false;
      }
    },
    [addPostMutation, communityId, creatorId],
  );
};
